// src/pages/Vistoria/respondervistoria.jsx
import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Menu from "../../comum/Menu/menu.jsx";
import Footer from "../../comum/Footer/index.jsx";
import { api } from '../../services/api.js';
import Swal from 'sweetalert2';
import {
   FaCamera,
   FaImage,
   FaCheckCircle,
   FaExclamationTriangle,
   FaEdit,
   FaClipboardList
} from 'react-icons/fa';
import './vistoria.css';

// =========================
// COMPONENTE FOTO (SUB-RECURSO)
// =========================
const FotoItem = ({ idItem, tipoFoto, titulo, onZoom, isEditable, onFileSelect }) => {
   const [foto, setFoto] = useState(null);
   const [loading, setLoading] = useState(true);
   const fileInputRef = useRef(null);

   useEffect(() => {
      const carregarFoto = async () => {
         try {
            const res = await api.get(`/vistoria/foto/${idItem}`);
            if (res.data && res.data.length > 0) {
               const dados = res.data[0];
               setFoto(tipoFoto === 'antes' ? dados.foto_antes : dados.foto_depois);
            }
         } catch (e) {
            console.error("Erro ao carregar foto:", e);
         } finally {
            loading && setLoading(false);
         }
      };
      carregarFoto();
   }, [idItem, tipoFoto]);

   const handleClick = () => {
      if (isEditable && !foto) {
         fileInputRef.current.click();
      } else if (foto) {
         onZoom(`data:image/jpeg;base64,${foto}`);
      }
   };

   return (
      <div className="text-center">
         <label className="x-small fw-bold text-muted text-uppercase d-block mb-1">
            {titulo}
         </label>
         <div 
            className="rounded-3 border bg-light d-flex align-items-center justify-content-center shadow-xs position-relative"
            style={{ height: '140px', overflow: 'hidden', cursor: 'pointer' }}
            onClick={handleClick}
         >
            {loading ? (
               <div className="spinner-border spinner-border-sm text-custom-red"></div>
            ) : foto ? (
               <img src={`data:image/jpeg;base64,${foto}`} className="img-fluid" style={{ height: '100%', width: '100%', objectFit: 'cover' }} alt={titulo} />
            ) : (
               <div className="text-muted small text-center px-2">
                  {isEditable ? (
                     <FaCamera className="d-block mx-auto mb-1 opacity-50 text-custom-red fs-4" />
                  ) : (
                     <FaImage className="d-block mx-auto mb-1 opacity-50 fs-4" />
                  )}
                  <span className="fw-semibold x-small d-block">
                     {isEditable ? 'TIRAR FOTO' : 'SEM FOTO'}
                  </span>
               </div>
            )}
         </div>
         {isEditable && (
            <input
               type="file" accept="image/*" capture="environment"
               ref={fileInputRef} style={{ display: 'none' }}
               onChange={(e) => onFileSelect(e.target.files[0])}
            />
         )}
      </div>
   );
};

// =========================
// COMPONENTE PRINCIPAL
// =========================
const VistoriaResposta = () => {
   const { token } = useParams();
   const navigate = useNavigate();
   const [vistoria, setVistoria] = useState(null);
   const [itens, setItens] = useState([]);
   const [loading, setLoading] = useState(false);
   const [zoomImg, setZoomImg] = useState(null);

   const decodeBase64 = (str) => {
      if (!str || str === "null") return "";
      try {
         return decodeURIComponent(escape(window.atob(str.trim())));
      } catch (e) {
         return str;
      }
   };

   useEffect(() => {
      if (!localStorage.getItem("chapa")) {
         navigate(`/?origem=vistoria-execucao&token=${token}`);
         return;
      }
      if (token) carregarDados();
   }, [token]);

   const carregarDados = async () => {
      setLoading(true);
      try {
         const res = await api.get(`/vistoria/token/${token}`);
         if (res.data && res.data.cabecalho) {
            setVistoria(res.data.cabecalho);

            const itensTratados = (res.data.itens || []).map(it => ({
               ...it,
               descricao_ponto: decodeBase64(it.descricao_ponto)
            }));

            setItens(itensTratados);
         }
      } catch (err) {
         Swal.fire({
            icon: 'error',
            title: 'Erro',
            text: 'Falha ao carregar vistoria.',
            confirmButtonColor: '#991b1b'
         });
      } finally {
         setLoading(false);
      }
   };

   const responderItem = async (item, arquivoFoto = null) => {
      setLoading(true);
      try {
         const resFoto = await api.get(`/vistoria/foto/${item.id_item}`);
         const dadosFoto = resFoto.data && resFoto.data.length > 0 ? resFoto.data[0] : null;

         if (dadosFoto && dadosFoto.foto_antes) {
            if (!dadosFoto.foto_depois && !arquivoFoto) {
               setLoading(false);
               Swal.fire({
                  title: 'Foto Obrigatória',
                  text: 'Este item possui uma foto "ANTES". Clique no quadro "SOLUÇÃO" para tirar a foto obrigatória antes de responder.',
                  icon: 'warning',
                  confirmButtonText: 'Entendido',
                  confirmButtonColor: '#991b1b'
               });
               return;
            }
         }
      } catch (error) {
         console.error("Erro ao validar fotos:", error);
      }
      setLoading(false);

      const { value: obs } = await Swal.fire({
         title: 'Responder Item',
         html: `<textarea id="swal-obs" class="form-control" rows="4" placeholder="Descreva a solução efetuada...">${item.obs_gerente || ''}</textarea>`,
         showCancelButton: true,
         confirmButtonText: 'Salvar Resposta',
         cancelButtonText: 'Cancelar',
         confirmButtonColor: '#991b1b',
         cancelButtonColor: '#64748b',
         customClass: { popup: 'rounded-4' },
         preConfirm: () => document.getElementById('swal-obs').value
      });

      if (obs !== undefined) {
         processarEnvio(item, obs, arquivoFoto);
      }
   };

   const processarEnvio = async (item, obs, file) => {
      setLoading(true);
      try {
         let fotoBase64 = "";
         if (file) {
            const reader = new FileReader();
            fotoBase64 = await new Promise((resolve) => {
               reader.onload = () => resolve(reader.result.split(',')[1]);
               reader.readAsDataURL(file);
            });
         }

         await api.put('/vistoria/executar', {
            id_item: item.id_item,
            id_vistoria: vistoria.id_vistoria,
            obs: obs,
            foto: fotoBase64,
            id_gerente: localStorage.getItem("chapa")
         });

         Swal.fire({
            icon: 'success',
            title: 'Sucesso',
            text: 'Resposta salva com sucesso!',
            confirmButtonColor: '#991b1b'
         });
         carregarDados();
      } catch (err) {
         Swal.fire('Erro', 'Não foi possível salvar.', 'error');
         console.error(err);
      } finally {
         setLoading(false);
      }
   };

   return (
      <div className="vistoria-page-wrapper">
         <Menu modulo="ARBOR" />

         <div className="container-fluid px-2 px-md-3 mt-4 max-width-container pb-5">
            {vistoria && (
               <div className="card border-0 shadow-sm mb-3 bg-white rounded-3 border-start-custom-red">
                  <div className="card-body py-3 px-3">
                     <h6 className="fw-bold mb-1 text-dark d-flex align-items-center gap-2">
                        <FaClipboardList className="text-custom-red" />
                        Vistoria #${vistoria.id_vistoria}
                     </h6>
                     <span className="small text-muted d-block">
                        {vistoria.razao}
                     </span>
                  </div>
               </div>
            )}

            <div className="row mx-0 g-3">
               {itens.map(item => (
                  <div className="col-12 px-0" key={item.id_item}>
                     <div className="card border-0 shadow-sm rounded-3 bg-white">
                        <div className="card-body p-3">
                           <div className="d-flex justify-content-between mb-2">
                              <span className="badge bg-light text-dark border small">{item.tipo}</span>
                              <span className={`small fw-bold ${item.status_item === 'EXECUTADO' ? 'text-success' : 'text-warning'}`}>
                                 {item.status_item === 'EXECUTADO' ? '● CONCLUÍDO' : '○ PENDENTE'}
                              </span>
                           </div>

                           <div className="mb-3 fw-bold text-dark text-break">
                              {item.descricao_ponto && item.descricao_ponto.includes('[RÉPLICA]:') ? (
                                 <>
                                    <div>{item.descricao_ponto.split('[RÉPLICA]:')[0].replace(/<\/?b>/gi, '')}</div>

                                    <div className="alert alert-danger mt-2 mb-0 p-2 small fw-bold rounded-3 d-flex align-items-center gap-2">
                                       <FaExclamationTriangle />
                                       [RÉPLICA]: {item.descricao_ponto.split('[RÉPLICA]:')[1].replace(/<\/?b>/gi, '')}
                                    </div>
                                 </>
                              ) : (
                                 item.descricao_ponto ? item.descricao_ponto.replace(/<\/?b>/gi, '') : ''
                              )}
                           </div>

                           <div className="row g-2 mb-3 mx-0">
                              <div className="col-6 px-1">
                                 <FotoItem idItem={item.id_item} tipoFoto="antes" titulo="ANTES" onZoom={setZoomImg} />
                              </div>
                              <div className="col-6 px-1">
                                 <FotoItem
                                    idItem={item.id_item} tipoFoto="depois" titulo="SOLUÇÃO"
                                    onZoom={setZoomImg} isEditable={item.status_item !== 'EXECUTADO'}
                                    onFileSelect={(file) => responderItem(item, file)}
                                 />
                              </div>
                           </div>

                           {item.obs_gerente && (
                              <div className="alert alert-success mt-3 mb-0 small text-break rounded-3">
                                 <strong>{item.data_execucao}</strong> {' - '}
                                 <strong>Usuário:</strong> {item.nome} {' - '}
                                 <strong>Solução:</strong> {item.obs_gerente}
                              </div>
                           )}

                           {item.status_item !== 'EXECUTADO' && (
                              <button 
                                 className="btn btn-custom-red w-100 fw-bold shadow-xs mt-3 rounded-3 d-flex align-items-center justify-content-center gap-2 py-2"
                                 onClick={() => responderItem(item)}
                              >
                                 <FaEdit /> RESPONDER SOLUÇÃO
                              </button>
                           )}
                        </div>
                     </div>
                  </div>
               ))}
            </div>
         </div>

         {/* MODAL ZOOM FOTO */}
         {zoomImg && (
            <div 
               style={{ 
                  position: 'fixed', inset: 0, 
                  backgroundColor: 'rgba(15,23,42,0.9)', 
                  backdropFilter: 'blur(4px)',
                  zIndex: 10000, 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  padding: '16px' 
               }}
               onClick={() => setZoomImg(null)}
            >
               <img src={zoomImg} style={{ maxWidth: '98%', maxHeight: '98%', borderRadius: '8px' }} alt="Zoom" />
            </div>
         )}

         {/* SPINNER LOADING */}
         {loading && (
            <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(15,23,42,0.6)', backdropFilter: 'blur(2px)', zIndex: 9999, display: 'flex', alignItems: 'center', justify: 'center' }}>
               <div className="spinner-border text-custom-red" role="status" style={{ width: '3rem', height: '3rem' }}></div>
            </div>
         )}

         <Footer />
      </div>
   );
};

export default VistoriaResposta;